import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import {
  EMAIL_RECIPIENT_HARD_CAP,
  checkSuppressedRecipients,
  deleteConnector,
  getEmailConnectorConfig,
  isEmailConnector,
  isValidEmail,
  listConnectors,
  normalizeRecipientList,
  setConnectorEnabled,
  testConnector,
  type ContentConnectorType,
  type ContentPublishingConnector,
} from "@/lib/composerCore";
import { useWordPressGateway } from "./useWordPressGateway";
import {
  emptyEditor,
  type ContentPublishingdispatchesAlias,
  type EditorState,
} from "@/components/composer/connectorManagerHelpers";
import {
  connectorToEditorState,
  saveConnectorFromEditor,
} from "@/components/composer/connectorEditorActions";
import { ConnectorRow } from "@/components/composer/ConnectorRow";
import { ConnectorManagerHeader } from "@/components/composer/connectorManager/ConnectorManagerHeader";
import { ConnectorEditorDialog } from "@/components/composer/connectorManager/ConnectorEditorDialog";
import { useConnectorOpenIndicator } from "@/components/composer/connectorManager/useConnectorOpenIndicator";

type Props = {
  workspaceId: string;
  initialConnectors: ContentPublishingConnector[];
  dispatches?: ContentPublishingdispatchesAlias;
  canManage: boolean;
  onChange?: (connectors: ContentPublishingConnector[]) => void;
};

export const ConnectorManager = ({
  workspaceId,
  initialConnectors,
  dispatches = [],
  canManage,
  onChange,
}: Props) => {
  const [connectors, setConnectors] = useState<ContentPublishingConnector[]>(initialConnectors);
  const [editor, setEditor] = useState<EditorState>(emptyEditor);
  const [savingEditor, setSavingEditor] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [suppressedByConnector, setSuppressedByConnector] = useState<Record<string, string[]>>({});

  const wp = useWordPressGateway(workspaceId, editor.open, editor.connectorType);

  const openEdit = (connector: ContentPublishingConnector) => {
    setEditor(connectorToEditorState(connector));
  };

  const {
    openIndicator,
    setOpenIndicator,
    readyDurationSec,
    setReadyDurationSec,
    liveAnnouncement,
  } = useConnectorOpenIndicator({ connectors, editor, openEdit });

  useEffect(() => {
    setConnectors(initialConnectors);
  }, [initialConnectors]);

  // Refresh suppression info for email connectors when the list changes.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const emailConnectors = initialConnectors.filter(isEmailConnector);
      const next: Record<string, string[]> = {};
      for (const c of emailConnectors) {
        const cfg = getEmailConnectorConfig(c);
        if (cfg.recipients.length === 0) continue;
        try {
          const suppressed = await checkSuppressedRecipients(workspaceId, cfg.recipients);
          if (cancelled) return;
          if (suppressed.length > 0) next[c.id] = suppressed;
        } catch {
          // Non-blocking: suppression info is informational
        }
      }
      if (!cancelled) setSuppressedByConnector(next);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [initialConnectors, workspaceId]);

  const sorted = useMemo(
    () => [...connectors].sort((a, b) => a.display_name.localeCompare(b.display_name)),
    [connectors],
  );

  const updateLocal = (next: ContentPublishingConnector[]) => {
    setConnectors(next);
    onChange?.(next);
  };

  const refresh = async () => {
    try {
      const next = await listConnectors(workspaceId);
      updateLocal(next);
    } catch (err) {
      toast.error((err as Error).message ?? "Failed to load connectors");
    }
  };

  const openCreate = (type: ContentConnectorType = "webhook") => {
    setEditor({ ...emptyEditor, open: true, connectorType: type });
  };

  const closeEditor = () => setEditor(emptyEditor);

  const addRecipientFromDraft = () => {
    const tokens = editor.recipientDraft
      .split(/[\s,;]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    if (tokens.length === 0) return;
    const invalid = tokens.filter((t) => !isValidEmail(t));
    if (invalid.length > 0) {
      toast.error(`Invalid email${invalid.length > 1 ? "s" : ""}: ${invalid.slice(0, 3).join(", ")}`);
      return;
    }
    const merged = normalizeRecipientList([...editor.recipients, ...tokens]);
    if (merged.length > EMAIL_RECIPIENT_HARD_CAP) {
      toast.error(`Maximum ${EMAIL_RECIPIENT_HARD_CAP} recipients per connector`);
      return;
    }
    setEditor((s) => ({ ...s, recipients: merged, recipientDraft: "" }));
  };

  const removeRecipient = (email: string) => {
    setEditor((s) => ({ ...s, recipients: s.recipients.filter((r) => r !== email) }));
  };

  const handleSave = async () => {
    setSavingEditor(true);
    try {
      const result = await saveConnectorFromEditor({ workspaceId, editor, wpLinked: wp.wpLinked });
      if ("errorMessage" in result) {
        toast.error(result.errorMessage);
      } else {
        const { saved, successMessage } = result;
        const next = editor.connector
          ? connectors.map((c) => (c.id === saved.id ? saved : c))
          : [...connectors, saved];
        updateLocal(next);
        toast.success(successMessage);
        closeEditor();
      }
    } finally {
      setSavingEditor(false);
    }
  };

  const handleTest = async (connector: ContentPublishingConnector) => {
    try {
      setBusyId(connector.id);
      const result = await testConnector(workspaceId, connector.id);
      const next = connectors.map((c) => (c.id === result.connector.id ? result.connector : c));
      updateLocal(next);
      const status = (result.dispatch?.response_snapshot as { status_code?: number } | null)?.status_code;
      if (result.ok) {
        if (isEmailConnector(connector)) {
          const cfg = getEmailConnectorConfig(connector);
          toast.success(
            connector.status === "disabled" || connector.status === "error"
              ? `Test email queued — connector re-enabled (${cfg.recipients.length} recipient${cfg.recipients.length === 1 ? "" : "s"})`
              : `Test email queued (${cfg.recipients.length} recipient${cfg.recipients.length === 1 ? "" : "s"})`,
          );
        } else {
          toast.success(
            connector.status === "disabled" || connector.status === "error"
              ? `Test ping succeeded — connector re-enabled${status ? ` (HTTP ${status})` : ""}`
              : `Test ping succeeded${status ? ` (HTTP ${status})` : ""}`,
          );
        }
      } else {
        if (isEmailConnector(connector)) {
          toast.error(`Test email failed${result.dispatch?.error_message ? `: ${result.dispatch.error_message}` : ""}`);
        } else {
          toast.error(`Test ping failed${status ? ` (HTTP ${status})` : ""}`);
        }
      }
    } catch (err) {
      toast.error((err as Error).message ?? "Test failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleToggle = async (connector: ContentPublishingConnector) => {
    try {
      setBusyId(connector.id);
      const enabled = connector.status !== "active";
      const updated = await setConnectorEnabled(workspaceId, connector.id, enabled);
      updateLocal(connectors.map((c) => (c.id === updated.id ? updated : c)));
      toast.success(enabled ? "Connector enabled" : "Connector disabled");
    } catch (err) {
      toast.error((err as Error).message ?? "Could not update connector");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (connector: ContentPublishingConnector) => {
    if (!window.confirm(`Delete "${connector.display_name}"? This cannot be undone.`)) return;
    try {
      setBusyId(connector.id);
      await deleteConnector(workspaceId, connector.id);
      updateLocal(connectors.filter((c) => c.id !== connector.id));
      toast.success("Connector deleted");
    } catch (err) {
      toast.error((err as Error).message ?? "Could not delete connector");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card className="shadow-soft">
      {/* Single, page-level live region for connector open/ready announcements.
          A dedicated `assertive` region that mounts once (always present in
          the DOM) is much more reliable for screen readers than per-row
          regions that toggle in and out — text changes here are reliably
          announced. */}
      <div role="status" aria-live="assertive" aria-atomic="true" className="sr-only">
        {liveAnnouncement}
      </div>
      <ConnectorManagerHeader
        canManage={canManage}
        onRefresh={() => void refresh()}
        onCreate={openCreate}
        readyDurationSec={readyDurationSec}
        setReadyDurationSec={setReadyDurationSec}
      />
      <CardContent className="space-y-3">
        {!canManage && (
          <p className="rounded-md border border-dashed border-border bg-muted/40 p-3 text-sm text-muted-foreground">
            Your current plan can view connectors but cannot create or send. Upgrade to enable outbound publishing.
          </p>
        )}
        {sorted.length === 0 ? (
          <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            No connectors yet. Add a webhook or email destination to dispatch prepared publications.
          </p>
        ) : (
          <ul className="space-y-2">
            {sorted.map((connector) => (
              <ConnectorRow
                key={connector.id}
                connector={connector}
                dispatches={dispatches}
                busy={busyId === connector.id}
                suppressed={suppressedByConnector[connector.id]}
                openIndicator={openIndicator}
                canManage={canManage}
                onTest={(c) => void handleTest(c)}
                onEdit={openEdit}
                onToggle={(c) => void handleToggle(c)}
                onDelete={(c) => void handleDelete(c)}
                onDismissIndicator={() => setOpenIndicator(null)}
              />
            ))}
          </ul>
        )}
      </CardContent>

      <ConnectorEditorDialog
        editor={editor}
        setEditor={setEditor}
        savingEditor={savingEditor}
        closeEditor={closeEditor}
        onSave={() => void handleSave()}
        wp={wp}
        addRecipientFromDraft={addRecipientFromDraft}
        removeRecipient={removeRecipient}
      />
    </Card>
  );
};

export default ConnectorManager;
